import { Box, Grid, GridItem, Text } from '@chakra-ui/react'
import React, { useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import moment from 'moment'
import type { EmbeddedMetadataValues } from '../../../../interfaces/FileDetailsResponse'
import SearchBar from '../../../SearchBar'

const INITIAL_ITEMS_LENGTH = 6

interface EmbeddedMetadataProps {
  data: EmbeddedMetadataValues | null
}

const EmbeddedMetadata: React.FC<EmbeddedMetadataProps> = ({ data }) => {
  const { t } = useTranslation()
  const [moreItemsVisible, toggleMoreItems] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')

  const formatString = (key: string, val: unknown): string => {
    switch (true) {
      case [
        'SubSecDateTimeOriginal',
        'DateTimeCreated',
        'DateTimeOriginal',
      ].includes(key): {
        return moment(val as string).format('ddd, MMMM Do YYYY, HH:mm:ss')
      }
      case key === 'TimeCreated': {
        return moment(val as string, 'HH:mm:ssZ').format('HH:mm:ss')
      }
      case ['ModifyDate', 'ArtworkDateCreated', 'DateCreated'].includes(key): {
        return moment(val as string).format('MMMM Do YYYY')
      }
      default:
        return String(val)
    }
  }

  const itemsData = useMemo(
    () => (data ? Object.entries(data) : []),
    [data],
  )

  const filteredItems = useMemo(
    () =>
      itemsData.filter(([key, val]) =>
        searchQuery
          ? key.toUpperCase().includes(searchQuery.toUpperCase()) ||
            JSON.stringify(val).toUpperCase().includes(searchQuery.toUpperCase()) ||
            formatString(key, val).toUpperCase().includes(searchQuery.toUpperCase())
          : true,
      ),
    [itemsData, searchQuery],
  )

  const initialItems = useMemo(
    () => filteredItems.slice(0, INITIAL_ITEMS_LENGTH),
    [filteredItems],
  )

  const moreItems = useMemo(
    () => filteredItems.slice(INITIAL_ITEMS_LENGTH),
    [filteredItems],
  )

  const totalItems = filteredItems.length

  return (
    <Box>
      <Text fontWeight="600" mb="12px">
        {t('embedded_metadata', 'Embedded Metadata')}
      </Text>

      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        padding="0 0 1em 0"
      />

      {filteredItems.length === 0 ? (
        <Text color="gray.500">
          {t('no_data_available', 'No data available')}
        </Text>
      ) : (
        <Grid
          templateColumns="160px 1fr"
          rowGap="12px"
          columnGap="16px"
          alignItems="start"
        >
          {initialItems.map(([key, val]) => (
            <React.Fragment key={key}>
              <GridItem>
                <Text color="#4b5a74" wordBreak="break-word">
                  {key}
                </Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="500" wordBreak="break-word">
                  {formatString(key, val)}
                </Text>
              </GridItem>
            </React.Fragment>
          ))}

          {moreItemsVisible &&
            moreItems.map(([key, val]) => (
              <React.Fragment key={key}>
                <GridItem>
                  <Text color="#4b5a74" wordBreak="break-word">
                    {key}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="500" wordBreak="break-word">
                    {formatString(key, val)}
                  </Text>
                </GridItem>
              </React.Fragment>
            ))}

          {moreItems.length > 0 && (
            <GridItem colSpan={2}>
              <Text
                textAlign="right"
                cursor="pointer"
                color="blue.600"
                fontWeight="500"
                onClick={() => void toggleMoreItems(!moreItemsVisible)}
              >
                {moreItemsVisible ? (
                  t('see_less', '- See less')
                ) : (
                  <Trans t={t} i18nKey="see_all_items">
                    - See all {{ totalItems }} items
                  </Trans>
                )}
              </Text>
            </GridItem>
          )}
        </Grid>
      )}
    </Box>
  )
}

export default EmbeddedMetadata
